"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Error_1 = __importDefault(require("@mui/icons-material/Error"));
const Info_1 = __importDefault(require("@mui/icons-material/Info"));
const material_1 = require("@mui/material");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const axios_1 = __importDefault(require("axios"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const zod_1 = require("zod");
const strings_1 = require("../../../../constants/strings");
const uiConstants_1 = require("../../../../constants/uiConstants");
const sessionQuery_1 = require("../../../../queries/sessionQuery");
const zustandStore_1 = require("../../../../zustandStore");
const useEmailInput_1 = require("../loginform/hooks/useEmailInput");
const lexico = new lexico_1.default();
const registerSchema = zod_1.z.object({
    firstName: zod_1.z.string().min(1),
    lastName: zod_1.z.string().min(1),
    email: zod_1.z.email(),
    companyName: zod_1.z
        .string()
        .min(1)
        .max(40, { error: 'Company name must be at most 40 characters long.' })
        .regex(/^(?=.*[\dA-Za-z])[\d A-Za-z]*$/, {
        message: 'Company name cannot be just empty spaces.',
    }),
});
// AMEX
const RegistrationForm = () => {
    const { registration, setRegistrationCompanyName, setRegistrationLastName, setRegistrationFirstName, setRegistrationFormLoading, resetRegistrationForm, } = (0, zustandStore_1.useZustand)(state => ({
        ...state,
    }));
    const { firstName, lastName, companyName: stateCompanyName, isFormLoading } = registration;
    const { state: emailState, handleEmailChange, handleEmailValidation } = (0, useEmailInput_1.useEmailInput)();
    const navigate = (0, react_router_1.useNavigate)();
    const register = (0, sessionQuery_1.useRegisterQuery)();
    const isFormValid = registerSchema.safeParse({
        firstName,
        lastName,
        email: emailState.value,
        companyName: stateCompanyName,
    }).success;
    const theme = (0, material_1.useTheme)();
    const [searchParams] = (0, react_router_1.useSearchParams)();
    const companyName = searchParams.get('c') || '';
    const opportunityId = searchParams.get('o') || '';
    const { setNotification } = (0, zustandStore_1.useZustand)();
    (0, react_1.useEffect)(() => {
        if (companyName) {
            setRegistrationCompanyName(companyName);
        }
    }, [companyName]);
    const handleSubmit = (event) => {
        event.preventDefault();
        setRegistrationFormLoading(true);
        const payload = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            username: emailState.value.trim(),
            companyName: stateCompanyName.trim(),
            opportunityId,
        };
        register.mutate(payload, {
            onSuccess: async () => {
                void navigate('/signin/message', {
                    state: {
                        heading: lexico.trc('Component title', 'Success!'),
                        message: lexico.tr('If your email is not yet registered, we’ll send you an activation email with details on how to proceed.'),
                    },
                });
                setRegistrationFormLoading(false);
            },
            onError: (err) => {
                setRegistrationFormLoading(false);
                let errorMessage = lexico.tr('An unexpected error occurred, please try again later.');
                if (axios_1.default.isAxiosError(err) && err.response) {
                    const { status, data } = err.response;
                    if (status >= 400 && data.errorMessage) {
                        errorMessage = data.errorMessage;
                    }
                }
                setNotification([
                    {
                        type: strings_1.notificationStrings.ERROR,
                        message: errorMessage,
                    },
                ]);
            },
            onSettled: () => {
                resetRegistrationForm();
            },
        });
    };
    return ((0, jsx_runtime_1.jsxs)("form", { "data-testid": "form", style: {
            padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.tsGoUnit.tsUnit,
            marginBottom: 0,
        }, onSubmit: handleSubmit, children: [(0, jsx_runtime_1.jsx)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", children: (0, jsx_runtime_1.jsx)(material_1.TextField, { label: "First Name", style: { padding: 0, margin: 0 }, type: "text", required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", name: "FIRST_NAME", value: firstName, onChange: evt => {
                        setRegistrationFirstName(evt.target.value);
                    }, slotProps: {
                        htmlInput: {
                            'data-testid': 'firstName',
                        },
                        inputLabel: {
                            style: { fontWeight: theme.typography.tsFontWeight.default },
                        },
                    } }) }), (0, jsx_runtime_1.jsx)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", children: (0, jsx_runtime_1.jsx)(material_1.TextField, { label: "Last Name", style: { padding: 0, margin: 0 }, type: "text", required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", name: "LAST_NAME", value: lastName, onChange: evt => {
                        setRegistrationLastName(evt.target.value);
                    }, slotProps: {
                        htmlInput: {
                            'data-testid': 'lastName',
                        },
                        inputLabel: {
                            style: { fontWeight: theme.typography.tsFontWeight.default },
                        },
                    } }) }), (0, jsx_runtime_1.jsxs)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", error: emailState.isError, children: [(0, jsx_runtime_1.jsx)(material_1.TextField, { label: lexico.tr('Email'), style: { padding: 0, margin: 0 }, type: "email", required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", value: emailState.value, error: emailState.isError, onChange: e => handleEmailChange(e.target.value), onBlur: handleEmailValidation, slotProps: {
                            htmlInput: {
                                'data-testid': 'email',
                            },
                            inputLabel: {
                                style: { fontWeight: theme.typography.tsFontWeight.default },
                            },
                        } }), emailState.isError && ((0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { style: { marginLeft: 0 }, children: [(0, jsx_runtime_1.jsx)(Error_1.default, { fontSize: "small", style: { verticalAlign: 'middle' } }), " ", lexico.tr('Invalid email address')] }))] }), (0, jsx_runtime_1.jsxs)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", children: [(0, jsx_runtime_1.jsx)(material_1.TextField, { label: "Company Name", style: { padding: 0, margin: 0 }, type: "text", required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", name: "COMPANY_NAME", value: stateCompanyName, onChange: evt => {
                            setRegistrationCompanyName(evt.target.value);
                        }, slotProps: {
                            htmlInput: {
                                'data-testid': 'companyName',
                                minLength: 1,
                                maxLength: 40,
                                pattern: String.raw `^(?=.*[\dA-Za-z])[\d A-Za-z]*$`,
                                title: 'Company name must be at most 40 characters long and cannot be just empty spaces.',
                            },
                            inputLabel: {
                                style: { fontWeight: theme.typography.tsFontWeight.default },
                            },
                        } }), (0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { style: { marginLeft: 0 }, children: [(0, jsx_runtime_1.jsx)(Info_1.default, { color: "info", fontSize: "small", style: { verticalAlign: 'top' } }), ' ', lexico.tr('Company name must be at most 40 characters long, no special characters and cannot be just empty spaces.')] })] }), (0, jsx_runtime_1.jsx)(material_1.Button, { "data-testid": "submit", style: {
                    width: '100%',
                    fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
                }, color: uiConstants_1.UI_BUTTON_PRIMARY, disabled: !isFormValid, type: "submit", loading: isFormLoading, startIcon: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {}), variant: "contained", children: strings_1.sessionStrings.CONTINUE })] }));
};
exports.default = RegistrationForm;
